// admin-ui/src/features/c/pages/EditorPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabaseClient';
import LocationEditorForm from '../components/LocationEditorForm';
import AttributePanel from '../components/AttributePanel';

const lang = 'de';

const localName = (r) =>
  r?.[`name_${lang}`] || r?.name_de || r?.name_en || r?.name_it || r?.name_fr || r?.name_hr || '';

export default function EditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);

  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    let mounted = true;

    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth?.user?.id;

      if (!uid) {
        if (mounted) setUserRole(null);
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', uid)
        .maybeSingle();

      if (error) {
        console.error('load user role failed', error);
      }

      if (mounted) setUserRole(data?.role || 'user');
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const isPrivileged = userRole === 'admin' || userRole === 'editor';

  // Kategorien laden
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/api/categories?lang=${lang}`);
        const data = await res.json();
        setCategories(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('load categories failed', e);
        setCategories([]);
      }
    })();
  }, []);

  // Mapping: category_id -> Label
  const categoryNameById = useMemo(() => {
    const idsByNorm = {};
    const labelByNorm = {};

    for (const c of categories) {
      const label = (c.name_de || c.name_en || `#${c.id}`).trim();
      const norm = label.toLowerCase();

      if (!idsByNorm[norm]) idsByNorm[norm] = [];
      idsByNorm[norm].push(c.id);

      if (!labelByNorm[norm]) labelByNorm[norm] = label;
    }

    const m = {};

    Object.entries(idsByNorm).forEach(([norm, ids]) => {
      const label = labelByNorm[norm];
      ids.forEach((id) => {
        m[id] = label;
      });
    });

    return m;
  }, [categories]);

  // Deduplizierte Kategorie-Liste
  const dedupCategories = useMemo(() => {
    const byNorm = new Map();

    for (const c of categories) {
      const label = (c.name_de || c.name_en || `#${c.id}`).trim();
      const norm = label.toLowerCase();

      if (!byNorm.has(norm)) {
        byNorm.set(norm, { id: c.id, name_de: label });
      }
    }

    return Array.from(byNorm.values()).sort((a, b) =>
      (a.name_de || '').toLowerCase().localeCompare((b.name_de || '').toLowerCase())
    );
  }, [categories]);

  // Einzel-Datensatz laden
  async function load() {
    setLoading(true);

    try {
      const res = await fetch(`/api/locations?id=${encodeURIComponent(String(id))}&lang=${lang}`);
      const json = await res.json();

      if (json && json.id) {
        const ln = localName(json);

        setRow({
          ...json,
          name_de: json.name_de || ln,
          display_name: json.display_name || ln,
        });
      } else {
        setRow(null);
      }
    } catch (e) {
      console.error('load location failed', e);
      setRow(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (id) load();
  }, [id]);

  // Speichern
  async function onSave(patch) {
    try {
      const res = await fetch(`/api/c/locations/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });

      if (res.ok) {
        await load();
        alert('Gespeichert');
      } else {
        const j = await res.json().catch(() => null);
        alert('Fehler: ' + (j?.error || res.statusText));
      }
    } catch (e) {
      console.error('save failed', e);
      alert('Fehler beim Speichern');
    }
  }

  if (loading) return <div style={{ padding: 16 }}>Lade…</div>;
  if (!row) return <div style={{ padding: 16 }}>Nicht gefunden</div>;

  const titleName = localName(row) || row.display_name || '';

  return (
<div className="c-editor" style={{ maxWidth: 900, margin: '0 auto', padding: 16 }}>
  {/* Action-Bar */}
  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
    <button
      onClick={() => {
        if (isPrivileged) {
          navigate(-1);
        } else {
          navigate('/c');
        }
      }}
    >
      &larr; Zurück
    </button>

    {isPrivileged && (
      <>
        <Link
          to="/"
          style={{
            display: 'inline-block',
            background: '#0f1626',
            color: '#fff',
            borderRadius: 8,
            padding: '.45rem .8rem',
            textDecoration: 'none',
          }}
        >
          ← Menü
        </Link>

        <Link
          to={`/c/${row.id}/attributes`}
          style={{
            display: 'inline-block',
            background: '#ffffff',
            color: '#0f1626',
            border: '1px solid #0f1626',
            borderRadius: 8,
            padding: '.45rem .8rem',
            textDecoration: 'none',
            marginLeft: 8,
          }}
        >
          Attribute bearbeiten
        </Link>
      </>
    )}
  </div>

  <h1 style={{ fontSize: 20, margin: '8px 0 16px' }}>
    Marker bearbeiten · #{row.id}
    {titleName ? ` · ${titleName}` : ''}
  </h1>

  <LocationEditorForm
    value={row}
    onSave={onSave}
    categoryNameById={categoryNameById}
    categories={dedupCategories}
  />

  {isPrivileged && <AttributePanel locationId={row.id} />}
</div>
  );
}